import React, { ComponentType, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { TenantDiscoveryResponse } from 'api';
import { logIn } from 'common/config/urls';
import { IS_LOGGING_OUT_KEY, LOGGING_OUT_VALUE } from 'common/constants/auth';
import { useTenantDiscovery } from 'services/hooks';
import { useIsUserLoggedIn, useVerifyAccessToken } from 'services/hooks/auth';

interface CustomRouteElementProps {
  component?: ComponentType<any>;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: React.ReactNode }>
    | ((props: { children: React.ReactNode }) => React.ReactElement);
}

// Helper component that returns the element for use in Route components
export const CustomRouteElement = (props: CustomRouteElementProps) => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const verifyAccessToken = useVerifyAccessToken();

  // this state gates the rendering of the prop Component or redirection to LogIn while we determine
  // if tenant discovery is needed and resolve the flow to follow
  const [isPendingFlowResolution, setIsPendingFlowResolution] = useState(true);

  const { discoverTenantBySlug, needsDiscovery } = useTenantDiscovery({ location: globalThis.location.href });

  const [hasVerifiedToken, setHasVerifiedToken] = useState(false);
  const accessToken = localStorage.getItem('access');
  const location = useLocation();
  const isLoggingOut = localStorage.getItem(IS_LOGGING_OUT_KEY) === LOGGING_OUT_VALUE;

  const { layout: Layout, component: Component, isPrivate = false } = props;

  const hasAccessToken = Boolean(accessToken);

  useEffect(() => {
    if (hasAccessToken && !isUserLoggedIn && !hasVerifiedToken) {
      verifyAccessToken();
      setHasVerifiedToken(true);
    }
  }, [hasAccessToken, isUserLoggedIn, hasVerifiedToken, verifyAccessToken]);

  useEffect(() => {
    const verifyFlowToFollow = async () => {
      const flow = await discoverTenantBySlug();
      const postDiscoveryFlow = flow?.post_discovery_flow as string | undefined;
      if (flow && postDiscoveryFlow === TenantDiscoveryResponse.post_discovery_flow.SAML) {
        // browser redirect to SSO login URL
        globalThis.location.href = flow.login_url as string;
      } else if (flow && postDiscoveryFlow === TenantDiscoveryResponse.post_discovery_flow.PASSWORD) {
        setIsPendingFlowResolution(false);
      }
    };
    if (!isUserLoggedIn && !hasAccessToken && needsDiscovery && isPendingFlowResolution && !isLoggingOut) {
      verifyFlowToFollow();
    }
  }, [needsDiscovery, hasAccessToken, discoverTenantBySlug, isPendingFlowResolution, isUserLoggedIn, isLoggingOut]);

  useEffect(() => {
    // This executes after we've been redirected to logIn,
    // so we can remove the helper flag
    if (isLoggingOut) {
      localStorage.removeItem(IS_LOGGING_OUT_KEY);
    }
  }, [isLoggingOut]);

  if (needsDiscovery && isPendingFlowResolution && !accessToken && !isLoggingOut) {
    return null;
  }

  // Protect private page from unauthorized users
  if (!isUserLoggedIn && !accessToken && isPrivate) {
    return <Navigate to={logIn} state={{ from: location }} replace />;
  }

  if (Layout && Component) {
    return (
      <Layout>
        <Component />
      </Layout>
    );
  }

  return Component ? <Component /> : <></>;
};

// Legacy interface for backward compatibility
interface CustomRouteProps {
  path: string;
  component?: ComponentType<any>;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: React.ReactNode }>
    | ((props: { children: React.ReactNode }) => React.ReactElement);
  exact?: boolean;
}

// This is kept for compatibility but should not be used as direct child of Routes
const CustomRoute = (props: CustomRouteProps) => (
  // This component is deprecated in React Router v6 context
  // Use CustomRouteElement instead
  <CustomRouteElement {...props} />
);
export default CustomRoute;
